/**
 * The data a blog article page shows, as React Query options shared by the
 * page (src/pages/BlogArticle.tsx) and the build-time prerender
 * (src/entry-server.tsx). The build fetches exactly these queries and ships
 * the cache with the static HTML, so the browser's first render matches the
 * prerendered page and nothing is fetched again before the article shows.
 *
 * Keys do not include the interface language: the article row carries both
 * languages, and the site is English-first, so the build and the browser
 * agree on the key.
 */
import type { FetchQueryOptions, QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Article {
  id: string;
  title_el: string | null;
  title_en: string | null;
  slug: string | null;
  slug_en: string | null;
  slug_el: string | null;
  content_el: string | null;
  content_en: string | null;
  meta_description_el: string | null;
  meta_description_en: string | null;
  featured_image_url: string | null;
  keywords: string[] | null;
  reading_time_minutes: number | null;
  view_count: number;
  published_at: string | null;
  updated_at?: string | null;
  category: string | null;
  topic_id: string | null;
}

export interface ArticleCard {
  id: string;
  title_el: string | null;
  title_en: string | null;
  slug: string | null;
  slug_el: string | null;
  slug_en: string | null;
  featured_image_url: string | null;
  reading_time_minutes: number | null;
  category: string | null;
  view_count?: number | null;
  keywords?: string[] | null;
}

export interface ArticleImage {
  id: string;
  image_url: string;
  image_type: string | null;
  position_index: number;
  status: string;
  prompt: string | null;
}

const ARTICLE_COLUMNS =
  "id, title_el, title_en, slug, slug_en, slug_el, content_el, content_en, meta_description_el, meta_description_en, " +
  "featured_image_url, keywords, reading_time_minutes, view_count, published_at, updated_at, category, topic_id";

const CARD_COLUMNS =
  "id, title_el, title_en, slug, slug_el, slug_en, featured_image_url, reading_time_minutes, category, view_count, keywords";

/** Articles change a few times a day at most; no refetch the moment a page opens. */
const STALE = 10 * 60 * 1000;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Q<T> = FetchQueryOptions<T, Error, T, any>;

export const articleQuery = (slug: string): Q<Article> => ({
  queryKey: ["article", slug],
  staleTime: STALE,
  retry: 1,
  queryFn: async () => {
    // Any of the three slug columns, so an old or Greek link still finds the article.
    const { data, error } = await supabase
      .from("ai_articles")
      .select(ARTICLE_COLUMNS)
      .eq("status", "published")
      .or(`slug_en.eq.${slug},slug_el.eq.${slug},slug.eq.${slug}`)
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error("Article not found");
    return data as unknown as Article;
  },
});

export const popularArticlesQuery: Q<ArticleCard[]> = {
  queryKey: ["popular-articles"],
  staleTime: STALE,
  queryFn: async () => {
    const { data, error } = await supabase
      .from("ai_articles")
      .select(CARD_COLUMNS)
      .eq("status", "published")
      .order("view_count", { ascending: false })
      .order("published_at", { ascending: false })
      .limit(5);
    if (error) throw error;
    return (data ?? []) as unknown as ArticleCard[];
  },
};

/**
 * Five related articles. Articles carry no category today, so relatedness
 * comes from shared keywords first, then from a fixed spread over the rest of
 * the catalogue (the same article always gets the same picks, and different
 * articles get different ones). Linking every article to the five newest, as
 * before, sent all internal link equity to the same five pages.
 */
export const relatedArticlesQuery = (a: Pick<Article, "id" | "slug_en" | "slug" | "keywords" | "category">): Q<ArticleCard[]> => ({
  queryKey: ["related-articles", a.id],
  staleTime: STALE,
  queryFn: async () => {
    const picks: ArticleCard[] = [];
    const add = (rows: ArticleCard[] | null | undefined) => {
      for (const r of rows ?? []) {
        if (picks.length >= 5) break;
        if (r.id !== a.id && !picks.some((p) => p.id === r.id)) picks.push(r);
      }
    };
    if (a.keywords?.length) {
      const { data } = await supabase
        .from("ai_articles")
        .select(CARD_COLUMNS)
        .eq("status", "published")
        .neq("id", a.id)
        .overlaps("keywords", a.keywords)
        .order("published_at", { ascending: false })
        .limit(5);
      add(data as unknown as ArticleCard[]);
    }
    if (picks.length < 5 && a.category) {
      const { data } = await supabase
        .from("ai_articles")
        .select(CARD_COLUMNS)
        .eq("status", "published")
        .eq("category", a.category)
        .neq("id", a.id)
        .order("published_at", { ascending: false })
        .limit(5);
      add(data as unknown as ArticleCard[]);
    }
    if (picks.length < 5) {
      const { data } = await supabase
        .from("ai_articles")
        .select(CARD_COLUMNS)
        .eq("status", "published")
        .neq("id", a.id)
        .order("published_at", { ascending: false })
        .limit(80);
      add(spread((data ?? []) as unknown as ArticleCard[], a.slug_en || a.slug || a.id, 5));
    }
    return picks;
  },
});

/** n items from the pool, spaced out from a start that depends only on `seed`. */
export function spread<T>(pool: T[], seed: string, n: number): T[] {
  if (pool.length <= n) return pool;
  let hash = 0;
  for (const ch of seed) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  const step = Math.max(1, Math.floor(pool.length / n));
  const out: T[] = [];
  for (let i = 0; out.length < n && i < pool.length; i++) {
    const item = pool[(hash + i * step) % pool.length];
    if (!out.includes(item)) out.push(item);
  }
  return out;
}

export const articleImagesQuery = (articleId: string): Q<ArticleImage[]> => ({
  queryKey: ["article-images", articleId],
  staleTime: STALE,
  queryFn: async () => {
    // Visitors can read approved images only; asking for others returned nothing extra.
    const { data, error } = await supabase
      .from("ai_article_images")
      .select("id, image_url, image_type, position_index, status, prompt")
      .eq("article_id", articleId)
      .eq("status", "approved")
      .order("position_index", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) throw error;
    return (data ?? []) as ArticleImage[];
  },
});

/** A banner borrowed from a related article, for the few articles without one of their own. */
export const siblingBannerQuery = (a: Pick<Article, "id" | "topic_id" | "keywords" | "category">): Q<{ image_url: string; source: string } | null> => ({
  queryKey: ["sibling-banner", a.id],
  staleTime: STALE,
  queryFn: async () => {
    const base = () =>
      supabase.from("ai_articles").select("id, featured_image_url").eq("status", "published").neq("id", a.id).not("featured_image_url", "is", null);
    if (a.topic_id) {
      const { data } = await base().eq("topic_id", a.topic_id).limit(1);
      if (data?.[0]?.featured_image_url) return { image_url: data[0].featured_image_url, source: "sibling_featured" };
    }
    if (a.keywords?.length) {
      const { data } = await base().overlaps("keywords", a.keywords).order("published_at", { ascending: false }).limit(1);
      if (data?.[0]?.featured_image_url) return { image_url: data[0].featured_image_url, source: "keyword_match" };
    }
    if (a.category) {
      const { data } = await base().eq("category", a.category).order("published_at", { ascending: false }).limit(1);
      if (data?.[0]?.featured_image_url) return { image_url: data[0].featured_image_url, source: "category_match" };
    }
    return null;
  },
});

/** The banner an article shows: its own, an approved banner image, or null. */
export function ownBanner(article: Article, images: ArticleImage[] | undefined): string | null {
  return article.featured_image_url || images?.find((i) => i.image_type === "article_banner")?.image_url || null;
}

/**
 * Everything the article page renders on first paint, fetched into the cache.
 * Used by the build; dependent queries run after the article is known.
 */
export async function prefetchBlogArticle(client: QueryClient, slug: string): Promise<Article> {
  const article = await client.fetchQuery(articleQuery(slug));
  const [images] = await Promise.all([
    client.fetchQuery(articleImagesQuery(article.id)).catch(() => [] as ArticleImage[]),
    client.fetchQuery(popularArticlesQuery).catch(() => []),
    client.fetchQuery(relatedArticlesQuery(article)).catch(() => []),
  ]);
  if (!ownBanner(article, images)) await client.fetchQuery(siblingBannerQuery(article)).catch(() => null);
  return article;
}

/** Canonical slugs of every published article, newest first (for the build). */
export async function listPublishedArticleSlugs(): Promise<string[]> {
  const { data, error } = await supabase
    .from("ai_articles")
    .select("slug_en, slug")
    .eq("status", "published")
    .order("published_at", { ascending: false })
    .limit(1000);
  if (error) throw error;
  return Array.from(new Set((data ?? []).map((r) => (r.slug_en || r.slug || "").trim()).filter(Boolean)));
}

/* ------------------------------------------------------------- helpers */

const stripTags = (html: string) => html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

/** Words of the article text (tags and attributes do not count). */
export function articleWordCount(content: string | null | undefined): number {
  const text = stripTags(content || "");
  return text ? text.split(" ").length : 0;
}

/** Minutes at 230 words a minute, at least 1. The stored value is not trusted (some say 15 for a 900-word piece). */
export function readingMinutes(words: number): number {
  return Math.max(1, Math.round(words / 230));
}

/**
 * "30 September 2026": the calendar date in UTC, so the build (UTC) and every
 * visitor's browser, in any time zone, print the same day.
 */
export function formatArticleDate(iso: string | null | undefined, language: "en" | "el" = "en"): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(language === "el" ? "el-GR" : "en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}

const isGreek = (t: string) => /[Ͱ-Ͽἀ-῿]/.test(t);

/**
 * Alt text for an inline article image. The stored text is the prompt the
 * image was generated from: often several hundred characters and often in
 * the other language. Use it only when it is in the page's language, cut at
 * a word near 125 characters; otherwise describe the figure by the article.
 */
export function inlineImageAlt(prompt: string | null, title: string, index: number, type: string | null, language: "en" | "el"): string {
  const p = (prompt || "").replace(/\s+/g, " ").trim();
  if (p && isGreek(p) === (language === "el")) {
    if (p.length <= 125) return p;
    const cut = p.slice(0, 125);
    return `${cut.slice(0, Math.max(cut.lastIndexOf(" "), 80)).replace(/[,.;:\s]+$/, "")}…`;
  }
  const kind = type === "infographic" ? (language === "el" ? "Infographic" : "Infographic") : language === "el" ? "Εικόνα" : "Illustration";
  return `${kind} ${index + 1}: ${title}`;
}
